import { computed, Injectable } from '@angular/core';
import { Association, AssociationKeys, LookupKeys } from '@ag-common-lib/public-api';
import { BehaviorSubject, Observable, combineLatest, map, shareReplay } from 'rxjs';
import { set } from 'lodash';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { LookupsService } from 'ag-common-svc/public-api';
import { toSignal } from '@angular/core/rxjs-interop';

@Injectable()
export class SelectAssociationService {
  private associationTypeLookup$ = this.lookupsService.associationTypeLookup$;
  private associationTypeLookupSig = toSignal(this.associationTypeLookup$);
  readonly associationTypeMapSig = computed(() => {
    const associationTypeLookup = this.associationTypeLookupSig();
    const map = new Map();

    associationTypeLookup?.forEach(associationType => {
      const dbId = associationType?.[BaseModelKeys.dbId];
      const description = associationType?.[LookupKeys.description];

      map.set(dbId, description);
    });

    return map;
  });

  constructor(private lookupsService: LookupsService) {}

  pickAssociationKey = (keyExp, association: Partial<Association>) => {
    if (typeof keyExp === 'string') {
      return association?.[keyExp] ?? null;
    }

    if (typeof keyExp === 'function') {
      return keyExp(association) ?? null;
    }

    return association?.[BaseModelKeys.dbId]
      ? [association?.[AssociationKeys.firstName], association?.[AssociationKeys.lastName]]
          .join('')
          .replace(/\s/g, '')
          .toLocaleLowerCase()
      : null;
  };

  updateAssociation = (
    formData,
    association: Partial<Association>,
    propertiesToAssign = null,
    onAssociationChanged,
  ) => {
    if (association[BaseModelKeys.dbId] === formData?.[BaseModelKeys.dbId]) {
      return null;
    }

    Object.keys(formData).forEach(key => {
      if (!new Set(Object.values(AssociationKeys)).has(key as any)) {
        return;
      }
      switch (key) {
        case AssociationKeys.dietaryConsideration:
          Object.keys(formData?.[AssociationKeys.dietaryConsideration] ?? {}).forEach(dietaryConsiderationKey => {
            set(formData, [AssociationKeys.dietaryConsideration, dietaryConsiderationKey], null);
          });
          return;
        case AssociationKeys.tShirtSizes:
          Object.keys(formData?.[AssociationKeys.tShirtSizes] ?? {}).forEach(tShirtSizesKey => {
            set(formData, [AssociationKeys.tShirtSizes, tShirtSizesKey], null);
          });
          return;

        default:
          set(formData, key, null);
          return;
      }
    });
    Object.entries(association).forEach(([key, value]) => {
      if (propertiesToAssign && !propertiesToAssign.has(key)) {
        return;
      }
      switch (key) {
        case AssociationKeys.dietaryConsideration:
          Object.entries(association?.[AssociationKeys.dietaryConsideration] ?? {}).forEach(
            ([dietaryConsiderationKey, dietaryConsiderationValue]) => {
              set(formData, [AssociationKeys.dietaryConsideration, dietaryConsiderationKey], dietaryConsiderationValue);
            },
          );
          return;
        case AssociationKeys.tShirtSizes:
          Object.entries(association?.[AssociationKeys.tShirtSizes] ?? {}).forEach(
            ([tShirtSizesKey, tShirtSizesValue]) => {
              set(formData, [AssociationKeys.tShirtSizes, tShirtSizesKey], tShirtSizesValue);
            },
          );
          return;

        default:
          set(formData, key, value);
          return;
      }
    });

    onAssociationChanged && onAssociationChanged(formData, association?.[BaseModelKeys.dbId]);
  };
}
